import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import i18next from "i18next";
import { routes } from "../utils/sideMenuRoutes";
import { brand } from "branchAndBrandSpecific/utils/brandVariables/brandVariables";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { Box, Flex, Heading, Text } from "@radix-ui/themes";
import { DashboardIcon } from "@radix-ui/react-icons";
import { useHasAuthRole } from "@/hooks/useAuthRole";
import { useAtomValue } from "jotai";
import { clientAtom } from "@/utils/atoms/atomClient";
import { Logitech_zoneRoutes } from "branchAndBrandSpecific/utils/brandVariables/zoneRoutes/Logitech_zoneRoutes";
import {
  Elgiganten_siteRoutes,
  LEGO_siteRoutes,
  Logitech_siteRoutes,
  BG_siteRoutes,
  DBG_siteRoutes,
} from "branchAndBrandSpecific/utils/brandVariables/siteRoutes/index";

export const SideMenuLinks: React.FunctionComponent<
  any & { closeWindow?: () => void }
> = ({ currentViewName, closeWindow }) => {
  const [menuRoutes, setMenuRoutes] = useState(routes);
  const currenUser = useCurrentUserProfile((s) => s.user);
  const hasAuthRole = useHasAuthRole();

  const isBestGamer = brand.brandValue === "BG";
  const isElgiganten = brand.brandValue === "ELGIGANTEN";
  const isLogitech = brand.brandValue === "LOGITECH";
  const isDBG = brand.brandValue === "DBG";
  const isLEGO = brand.brandValue === "LEGO";

  const currentClient = useAtomValue(clientAtom);

  useEffect(() => {
    if (isLEGO) {
      setMenuRoutes(LEGO_siteRoutes({ authRole: hasAuthRole }) as any);
    }
    if (isElgiganten) {
      setMenuRoutes(Elgiganten_siteRoutes({ authRole: hasAuthRole }) as any);
    }
    if (isLogitech) {
      setMenuRoutes(Logitech_siteRoutes({ authRole: hasAuthRole }) as any);
    }
    if (isBestGamer) {
      setMenuRoutes(BG_siteRoutes({ authRole: hasAuthRole }) as any);
    }
    if (isDBG) {
      setMenuRoutes(DBG_siteRoutes as any);
    }
  }, [brand.brandValue]);

  useEffect(() => {
    if (currentClient !== "") {
      setMenuRoutes(Logitech_zoneRoutes as any);
    }
  }, [currentClient]);

  return (
    <Flex className="primary-nav__nav" direction="column" gap="5">
      {Object.entries(menuRoutes).map(([key, value]) => (
        <Flex
          key={key}
          className="primary-nav__section"
          direction="column"
          gap="1"
        >
          {/* Heading */}
          {key.trim() && value[0]?.title && (
            <Box className="primary-nav__heading">
              <Heading as="h4" size="1" color="gray">
                {i18next.t(key)}
              </Heading>
            </Box>
          )}

          {/* Items */}
          <Flex className="primary-nav__list" gap="0" direction="column">
            {key === "routes.headlines.main" && (
              <Box
                asChild
                key={key}
                className={
                  currentViewName === "/home"
                    ? "primary-nav__item primary-nav__item--active"
                    : "primary-nav__item"
                }
                onClick={closeWindow}
              >
                <Link to="/home" className="primary-nav__link">
                  <Flex
                    gap="2"
                    align="center"
                    className="primary-nav__link--contents"
                  >
                    <DashboardIcon />
                    <Text size="2">{i18next.t("routes.route.home")}</Text>
                  </Flex>
                </Link>
              </Box>
            )}

            {value.map((v: any) => {
              if (v) {
                const isFallGuysLink = v.link === "/fallguys";
                const isForzaMotorsportLink = v.link === "/forzamotorsport";
                const isCSGOLink = v.link === "/counterstrike";
                const isCurrentlyForza =
                  currentViewName.includes("forza") &&
                  currentViewName.includes("challenges");
                const isCurrentlyFallGuys =
                  currentViewName.includes("fall-guys") &&
                  currentViewName.includes("challenges");
                const isCurrentlyCSGO =
                  currentViewName.includes("cs2") &&
                  currentViewName.includes("challenges");
                const isForza = isForzaMotorsportLink && isCurrentlyForza;
                const isFallGuys = isFallGuysLink && isCurrentlyFallGuys;
                const isCSGO = isCSGOLink && isCurrentlyCSGO;

                const isCurrentView =
                  currentViewName.includes("fall-guys") ||
                  currentViewName.includes("forza");
                v.link === "/forzamotorsport" && isCurrentView;

                // News logic to make menu item active when on news page or news article
                const isNews = currentViewName.includes("news");
                const isArticle =
                  (isNews && v.link === "/news") ||
                  (isNews &&
                    v.link.includes("zone") &&
                    v.link.includes("news"));

                // Challenges logic to make menu item active when on challenge page
                const isChallenges = currentViewName.includes("challenges");
                const isChallenge =
                  (isChallenges && v.link === "/challenges") ||
                  (isChallenges &&
                    v.link.includes("zone") &&
                    v.link.includes("challenges"));

                // Events logic to make menu item active when on event page
                const isEvents = currentViewName.includes("event");
                const isEvent =
                  (isEvents && v.link === "/events") ||
                  (isEvents &&
                    v.link.includes("zone") &&
                    v.link.includes("event"));

                return (
                  <Box
                    asChild
                    key={v.title}
                    className={`primary-nav__item ${
                      v.link === currentViewName && "primary-nav__item--active"
                    } ${
                      (isForza || isFallGuys || isCSGO) &&
                      "primary-nav__item--active"
                    } ${v.game ? `primary-nav__game game-${v.game}` : ""} ${
                      isArticle && "primary-nav__item--active"
                    } ${isChallenge && "primary-nav__item--active"} ${
                      isEvent && "primary-nav__item--active"
                    }`}
                  >
                    <Link
                      to={`${
                        v.link.includes("http")
                          ? `${v.link}/${currenUser.uid}_${currenUser.userName}`
                          : v.link
                      }`}
                      className="primary-nav__link"
                      onClick={closeWindow}
                      target={`${v.link.includes("http") ? "_blank" : ""}`}
                    >
                      <Flex gap="2" align="center">
                        {v.logo && <v.logo />}
                        <Text size="2">{i18next.t(v.title)}</Text>
                      </Flex>
                    </Link>
                  </Box>
                );
              }
            })}
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
};
